<template>
    <l-side-menu v-if="!survey.status || survey.status == 'draft'">
        <l-side-menu-picture :src="icon"></l-side-menu-picture>
        <l-side-menu-element
            v-for="item in menu"
            :key="item.path"
            :show="item.show"
            :type="item.type"
            :icon="item.icon"
            :label="item.label"
            :path="item.path"
            :handler="() => goTo(item.path)"
            :active="item.active"
        ></l-side-menu-element>
    </l-side-menu>
    <l-side-menu v-else>
        <l-side-menu-picture :src="icon"></l-side-menu-picture>

        <l-side-menu-element
            v-for="item in insightsMenus"
            :key="item.label"
            :active="item.active"
            :show="item.show"
            :label="item.label"
            :handler="item.handler"
            :icon="item.icon"
        ></l-side-menu-element>
        <span v-show="expand">
            <l-side-menu-element
                v-for="item in menu.filter(m => m.showInSub)"
                :key="item.path"
                :show="item.show"
                :type="item.type"
                :icon="item.icon"
                :label="item.label"
                :path="item.path"
                :handler="() => goTo(item.path)"
                :active="item.active"
            ></l-side-menu-element>
        </span>
    </l-side-menu>
</template>

<script>
import LSideMenu from "@/components/LSideMenu";
import LSideMenuPicture from "@/components/LSideMenuPicture";
import LSideMenuElement from "@/components/LSideMenuElement";
import icon from "@/img/icons/surveys.png";

export default {
    components: {
        LSideMenu,
        LSideMenuPicture,
        LSideMenuElement,
    },
    props: {
        survey: {
            type: Object,
            required: true,
        },
        rights: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            expand: false,
        };
    },
    computed: {
        insightsMenus() {
            const { survey, surveyId, $route, showInSights, goTo } = this;
            return [
                {
                    active: false,
                    show: true,
                    label: this.$t("surveys.details.menuBack"),
                    handler: () => goTo(`/surveys/list`),
                    icon: "fal fa-caret-left icon-light",
                },
                {
                    active: !$route.params.section,
                    show: showInSights,
                    label: this.$t("surveys.details.menuInsights"),
                    handler: () => goTo(`/surveys/insights/${surveyId}/`),
                    icon: "fal fa-analytics icon-light",
                },
                {
                    active: $route.params.section === "visibility",
                    show: survey.isOwner && survey.status != "draft",
                    label: this.$t("surveys.details.menuVisibility"),
                    handler: () => goTo(`/surveys/edit/${surveyId}/visibility`),
                    icon: "fal fa-eye icon-light",
                },
                {
                    active: false,
                    icon: `fal icon-light ${!this.expand ? "fa-cog" : "fa-caret-down"}`,
                    show: survey.isOwner,
                    label: this.$t("surveys.details.menuSettings"),
                    handler: () => (this.expand = !this.expand),
                },
            ];
        },
        menu() {
            return [
                {
                    show: true,
                    icon: "fal fa-caret-left icon-light",
                    label: this.$t("surveys.details.menuBack"),
                    path: "/surveys/list",
                },

                {
                    show: this.showInSights,
                    icon: "fal fa-analytics icon-light",
                    active: this.$route.params.section == "insights",
                    label: this.$t("surveys.details.menuInsights"),
                    path: `/surveys/insights/${this.surveyId}/`,
                },
                {
                    showInSub: true, // this prop determines if this menu should show when a survey is setup(aka status is not draft nor complete...)
                    show: true,
                    icon: "fal fa-edit",
                    active: this.$route.params.section == "basics",
                    label: this.$t("surveys.details.menuNameOwners"),
                    path: `/surveys/edit/${this.surveyId}/basics`,
                },

                {
                    showInSub: true,
                    show: true,
                    icon: "fal fa-question icon-light",
                    active: this.$route.params.section == "questions",
                    label: this.$t("surveys.details.menuQuestions"),
                    path: `/surveys/edit/${this.surveyId}/questions`,
                },
                {
                    showInSub: true,
                    show: true,
                    icon: "fal fa-calendar icon-light",
                    active: this.$route.params.section == "timing",
                    label: this.$t("surveys.details.menuTiming"),
                    path: `/surveys/edit/${this.surveyId}/timing`,
                },
                {
                    showInSub: true,
                    show: true,
                    icon: "fal fa-users icon-light",
                    active: this.$route.params.section == "participants",
                    label: this.$t("surveys.details.menuParticipants"),
                    path: `/surveys/edit/${this.surveyId}/participants`,
                },
                {
                    showInSub: true,
                    show: true,
                    icon: "fal fa-envelope icon-light",
                    active: this.$route.params.section == "notifications",
                    label: this.$t("surveys.details.menuEmail"), // notification
                    path: `/surveys/edit/${this.surveyId}/notifications`,
                },
                {
                    showInSub: true,
                    show: this.$route.params.id != "new", // we shouldn't show status when user wants to create a survey.
                    icon: "fal fa-rocket icon-light",
                    active: this.$route.params.section == "status",
                    label: this.$t("surveys.details.menuStatus"), // status
                    path: `/surveys/edit/${this.surveyId}/status`,
                },
            ];
        },

        surveyId() {
            return this.survey._id || "new";
        },
        showInSights() {
            if (!this.survey.status || this.survey.status === "draft") {
                return false;
            }

            return (
                this.permissionCheck("LIST") ||
                this.permissionCheck("HEAT_MAP") ||
                this.permissionCheck("MULTI") ||
                this.permissionCheck("COMMENTS")
            );
        },
    },
    created() {
        this.icon = icon;
        // set expand by checking the param, so the left pannel(menu) would expand when it's in setting mode
        if (
            ["basics", "timing", "participants", "questions", "notifications", "status"].includes(
                this.$route.params.section,
            )
        ) {
            this.expand = true;
        }
    },

    methods: {
        permissionCheck(tab) {
            if (this.rights.S_ACCESS_ALL) {
                return true;
            }
            return this.survey.sharedWithMe?.scope?.includes(tab);
        },
        goTo(url) {
            this.$router.push(url);
        },
    },
};
</script>

<style scoped></style>
